import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { resolveIntakeReview } from "../../services/intake";

// Malca (Orion email) pieces of the Email intake page: the review cards,
// the list of date moves intake made on its own (with Undo), and the dry
// run. Every button goes through the resolveIntakeReview callable.
// Design: docs/malca-intake-design.md §7, §9; sketch docs/mockups/malca-intake-phone.html.

export const isMalcaReview = (r) => r.profileId === "malca" || String(r.reason || "").startsWith("malca_");

const LABEL = {
  malca_cancel_job: { text: "cancel job?", cls: "chip-red" },
  malca_cancel_stop: { text: "cancel a stop?", cls: "chip-red" },
  malca_stop_missing: { text: "stop missing", cls: "chip-amber" },
  malca_manual: { text: "set up by hand", cls: "chip-amber" },
  malca_billing_note: { text: "billing note", cls: "chip-amber" },
  malca_note: { text: "note from Malca", cls: "chip-amber" },
  malca_date_moved: { text: "date moved", cls: "chip-amber" },
  malca_for_cancelled: { text: "job is cancelled", cls: "chip-red" },
  malca_after_complete: { text: "job already complete", cls: "chip-amber" },
  malca_unrecognized: { text: "didn't recognize", cls: "chip-amber" },
  malca_paperwork: { text: "receipt not placed", cls: "chip-amber" },
  ambiguous_match: { text: "2+ jobs have this number", cls: "chip-amber" },
  conflict: { text: "email disagrees with the job", cls: "chip-amber" },
};

const fmtDay = (ymd) => {
  if (!ymd) return "—";
  const [y, m, d] = String(ymd).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};
const fmtWhen = (ts) => (ts?.toDate ? ts.toDate().toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

const malcaChip = { background: "#f1e8fb", color: "#6b2fb3" };
const quoteBox = { fontSize: 12.5, color: "#5a4300", background: "#fffaeb", borderLeft: "3px solid #f0c75e", padding: "6px 9px", marginTop: 7, borderRadius: "0 6px 6px 0", fontStyle: "italic", whiteSpace: "pre-wrap" };
const FIELD = { numGuards: "guards", scheduledTime: "time", scheduledDate: "date" };

export function MalcaCard({ review, isPhone, onOpenJob, onCreateFromEmail }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const d = review.details || {};
  const label = LABEL[review.reason] || { text: review.reason, cls: "chip-amber" };
  const cancelled = new Set(review.cancelledStopIds || []);

  async function act(action, value) {
    setBusy(true);
    setErr("");
    try {
      await resolveIntakeReview(review.id, action, value);
    } catch (e) {
      setErr(e?.message || String(e));
    }
    setBusy(false);
  }

  const btn = isPhone ? { flex: 1, minHeight: 40, fontSize: 13 } : undefined;
  const red = { color: "#a32d2d", borderColor: "#e0a3a3" };
  const title = review.orderNo || (d.jobNumbers || [])[0] || "Orion email";

  return (
    <div style={{ background: "#fff", border: "1px solid #e3e6ea", borderRadius: 10, padding: "12px 14px", marginBottom: 8 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontWeight: 800, fontSize: 14.5 }}>{title}</span>
        <span className="chip" style={malcaChip}>Malca</span>
        <span className={`chip ${label.cls}`}>{label.text}</span>
        <span style={{ marginLeft: "auto", color: "#888", fontSize: 11 }}>{fmtWhen(review.createdAt)}</span>
      </div>

      {review.reason === "malca_date_moved" && (
        <>
          <div style={{ fontSize: 12.5, color: "#6b7684", marginTop: 3 }}>
            {d.complete ? "The job is already complete, so this needs you." : d.assigned ? "A guard is assigned, so this needs you." : "This needs you."}
          </div>
          <div style={{ fontWeight: 700, marginTop: 6 }}>{fmtDay(d.from)} → {fmtDay(d.to)}</div>
        </>
      )}
      {review.reason === "malca_cancel_job" && d.date && <div style={{ fontSize: 12.5, color: "#6b7684", marginTop: 3 }}>Job date {fmtDay(d.date)}</div>}
      {review.reason === "malca_stop_missing" && (
        <div style={{ fontSize: 12.5, color: "#5a4300", marginTop: 3 }}>
          {d.stopName || "A stop"} isn't in the latest email for this job. Cancelled, or left off?
        </div>
      )}
      {review.reason === "malca_manual" && (
        <div style={{ fontSize: 12.5, color: "#6b7684", marginTop: 3 }}>
          {d.date ? `${fmtDay(d.date)} · ` : ""}{d.why}. Intake didn't create anything.
        </div>
      )}
      {review.reason === "malca_after_complete" && (
        <div style={{ fontSize: 12.5, color: "#6b7684", marginTop: 3 }}>
          The job is complete, so nothing was changed. The email would have:
          {(d.changes || []).map((c) => <div key={c}>· {c.replace(/^Would /, "")}</div>)}
        </div>
      )}
      {review.reason === "conflict" && (d.conflicts || []).map((c) => (
        <div key={c.field} style={{ fontSize: 12.5, color: "#6b7684", marginTop: 3 }}>
          {FIELD[c.field] || c.field}: job has <b>{String(c.current)}</b>, email says <b>{String(c.proposed)}</b> (kept yours)
        </div>
      ))}
      {typeof d.why === "string" && review.reason !== "malca_manual" && <div style={{ fontSize: 12.5, color: "#6b7684", marginTop: 3 }}>{d.why}</div>}
      {d.quote && <div style={quoteBox}>"{d.quote}"</div>}

      {review.reason === "malca_cancel_stop" && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, color: "#6b7684" }}>
            {d.scope === "job" ? "This may mean the whole job. Cancel it below, or pick a stop." : "Which stop? Pick one, or cancel the whole job below."}
          </div>
          {(d.stops || []).map((s) => (
            <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "7px 0", borderTop: "1px solid #f0f2f5", fontSize: 13 }}>
              <span style={{ textDecoration: cancelled.has(s.id) ? "line-through" : "none", color: cancelled.has(s.id) ? "#999" : undefined }}>
                {s.name}{s.suggested ? " ⓘ named in the email" : ""}
                <div style={{ fontSize: 11.5, color: "#6b7684" }}>{[s.time, ...(s.receipts || [])].filter(Boolean).join(" · ")}</div>
              </span>
              {cancelled.has(s.id)
                ? <span className="chip chip-gray">cancelled</span>
                : <button className="btn btn-sm" style={red} disabled={busy} onClick={() => act("cancel_stop", s.id)}>Cancel stop</button>}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
        {review.reason === "malca_cancel_job" && (
          <>
            <button className="btn btn-sm" style={{ ...btn, ...red }} disabled={busy} onClick={() => onOpenJob(review.jobId)}>Cancel job…</button>
            <button className="btn btn-sm btn-blue" style={btn} disabled={busy} onClick={() => act("accept")}>Done</button>
            <button className="btn btn-sm" style={btn} disabled={busy} onClick={() => act("dismiss")}>Ignore</button>
          </>
        )}
        {review.reason === "malca_cancel_stop" && (
          <>
            <button className="btn btn-sm" style={{ ...btn, ...red }} disabled={busy} onClick={() => onOpenJob(review.jobId)}>Cancel whole job…</button>
            <button className="btn btn-sm btn-blue" style={btn} disabled={busy} onClick={() => act("accept")}>Done</button>
            <button className="btn btn-sm" style={btn} disabled={busy} onClick={() => act("dismiss")}>Ignore</button>
          </>
        )}
        {review.reason === "malca_stop_missing" && (
          <>
            <button className="btn btn-sm" style={{ ...btn, ...red }} disabled={busy} onClick={() => act("cancel_stop")}>Cancel this stop</button>
            <button className="btn btn-sm btn-blue" style={btn} disabled={busy} onClick={() => act("keep_stop")}>Keep it</button>
          </>
        )}
        {review.reason === "malca_date_moved" && (
          <>
            <button className="btn btn-sm btn-blue" style={btn} disabled={busy} onClick={() => act("move_date")}>Move it</button>
            <button className="btn btn-sm" style={btn} disabled={busy} onClick={() => act("keep_date")}>Keep {fmtDay(d.from)}</button>
          </>
        )}
        {review.reason === "malca_manual" && (
          <>
            {review.jobId && <button className="btn btn-sm" style={btn} disabled={busy} onClick={() => onOpenJob(review.jobId)}>Open job</button>}
            <button className="btn btn-sm btn-blue" style={btn} disabled={busy} onClick={() => onCreateFromEmail(d.emailText || "")}>Open in Create Job</button>
            <button className="btn btn-sm" style={btn} disabled={busy} onClick={() => act("dismiss")}>Dismiss</button>
          </>
        )}
        {review.reason === "malca_billing_note" && (
          <>
            <button className="btn btn-sm btn-blue" style={btn} disabled={busy} onClick={() => navigate("/owner/billing")}>Open Ready to bill</button>
            <button className="btn btn-sm" style={btn} disabled={busy} onClick={() => act("dismiss")}>Dismiss</button>
          </>
        )}
        {!["malca_cancel_job", "malca_cancel_stop", "malca_stop_missing", "malca_date_moved", "malca_manual", "malca_billing_note"].includes(review.reason) && (
          <>
            {review.reason === "malca_for_cancelled" && <button className="btn btn-sm btn-green" style={btn} disabled={busy} onClick={() => act("restore_job")}>Restore job</button>}
            {review.jobId && <button className="btn btn-sm" style={btn} disabled={busy} onClick={() => onOpenJob(review.jobId)}>Open job</button>}
            <button className="btn btn-sm btn-blue" style={btn} disabled={busy} onClick={() => act("accept")}>Looks right</button>
            <button className="btn btn-sm" style={btn} disabled={busy} onClick={() => act("dismiss")}>Dismiss</button>
          </>
        )}
        {err && <span style={{ fontSize: 11, color: "#a32d2d" }}>{err}</span>}
      </div>
    </div>
  );
}

// Date moves intake made on its own (untouched schedule jobs), newest first.
export function MovesList({ moves, secHead }) {
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState("");
  if (!moves.length) return null;
  async function undo(m) {
    setBusyId(m.id);
    setErr("");
    try { await resolveIntakeReview(m.id, "undo_move"); } catch (e) { setErr(e?.message || String(e)); }
    setBusyId(null);
  }
  return (
    <div>
      <div style={secHead}><span>Moved on its own</span><span>{moves.length}</span></div>
      <div style={{ background: "#fff", border: "1px solid #e3e6ea", borderRadius: 10, padding: "2px 14px", marginBottom: 8 }}>
        {moves.map((m) => (
          <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #f0f2f5" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{m.orderNo || m.details?.jobNumber}</div>
              <div style={{ fontSize: 12, color: "#6b7684" }}>{fmtDay(m.details?.from)} → {fmtDay(m.details?.to)} · {fmtWhen(m.createdAt)}</div>
            </div>
            <button className="btn btn-sm" disabled={busyId === m.id} onClick={() => undo(m)}>Undo</button>
          </div>
        ))}
        {err && <div style={{ fontSize: 11, color: "#a32d2d", padding: "6px 0" }}>{err}</div>}
      </div>
    </div>
  );
}

const ACTION = {
  create: { cls: "chip-green", text: "create" },
  update: { cls: "chip-blue", text: "add" },
  link: { cls: "chip-gray", text: "no change" },
  review: { cls: "chip-red", text: "card" },
  ignore: { cls: "chip-gray", text: "ignore" },
};

// What intake would do with each Orion email, one line per job number.
export function MalcaDryRun({ rows, live, secHead }) {
  const [showAll, setShowAll] = useState(false);
  if (!rows.length) return null;
  // Newest email first; rows of one email stay together.
  const byEmail = new Map();
  for (const r of rows) {
    if (!byEmail.has(r.emailId)) byEmail.set(r.emailId, []);
    byEmail.get(r.emailId).push(r);
  }
  const emails = [...byEmail.values()].sort((a, b) => String(b[0].sentAt || "").localeCompare(String(a[0].sentAt || "")));
  const shown = showAll ? emails : emails.slice(0, 8);
  return (
    <div>
      <div style={secHead}><span>Malca dry run · what intake would do</span><span>{emails.length}</span></div>
      {!live && (
        <div style={{ background: "#f1e8fb", border: "1px solid #d8c2f0", color: "#4b2180", borderRadius: 10, padding: "9px 12px", fontSize: 12.5, marginBottom: 8 }}>
          Dry run: nothing below has been written to jobs. Keep making Malca jobs by hand until you say "go live".
        </div>
      )}
      <div style={{ background: "#fff", border: "1px solid #e3e6ea", borderRadius: 10, padding: "2px 14px", marginBottom: 8 }}>
        {shown.map((group) => (
          <div key={group[0].emailId} style={{ padding: "8px 0", borderBottom: "1px solid #f0f2f5" }}>
            <div style={{ fontSize: 11.5, color: "#6b7684" }}>{group[0].subject} · {group[0].sentAt ? new Date(group[0].sentAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}</div>
            {group.sort((a, b) => a.id.localeCompare(b.id)).map((r) => {
              const a = ACTION[r.action] || { cls: "chip-gray", text: r.action };
              return (
                <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginTop: 4 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13.5 }}>{r.jobNumber || "Email"}{r.date ? ` · ${fmtDay(r.date)}` : ""}</div>
                    {(r.lines || []).map((l, i) => <div key={i} style={{ fontSize: 12.5, color: "#555" }}>{l}</div>)}
                  </div>
                  <span className={`chip ${a.cls}`} style={{ flexShrink: 0 }}>{a.text}</span>
                </div>
              );
            })}
          </div>
        ))}
        {emails.length > 8 && (
          <button onClick={() => setShowAll((v) => !v)} style={{ width: "100%", background: "none", border: "none", color: "#185FA5", fontWeight: 800, fontSize: 13, padding: "9px 0", cursor: "pointer" }}>
            {showAll ? "Show fewer" : `Show all ${emails.length}`}
          </button>
        )}
      </div>
    </div>
  );
}
